import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { NavLink as RouterLink } from "react-router-dom";
import { format } from "date-fns";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Edit, RemoveRedEye } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import Paging from "../../components/Layout/Paging";
import TableSkeleton from "../../components/Layout/TableSkeleton";
import Toast from "../../components/Layout/Toast";
import { StyledTableCell, StyledTableRow } from "../../styles/tableStyles";
import { changePageTitle, selectPreference } from "../preferences/preferencesSlice";
import { RoleTypes } from "../auth/types/roleTypes";
import { isPrivilegedTransaction } from "../../utils/authUtils";
import { selectAuth } from "../auth/authSlice";
import {
  fetchComplaints,
  selectComplaints,
} from "./complaintSlice";
import { ComplaintFilters, ComplaintFilterValues } from "./components/ComplaintFilters";
import { ComplaintStatusChip } from "./components/ComplaintStatusChip";

export const ComplaintsList = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");
  const { searchText } = useAppSelector(selectPreference);
  const { user } = useAppSelector(selectAuth);
  const roles = user?.roles ?? [];
  const {
    complaintsWithCount: { complaints, totalCount },
    loading,
    success,
    error,
  } = useAppSelector(selectComplaints);

  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(0);
  const [filters, setFilters] = useState<ComplaintFilterValues>({});

  const canCreate = isPrivilegedTransaction(roles, RoleTypes.OpenComplains);

  useEffect(() => {
    dispatch(changePageTitle(t("complaints")));
  }, [dispatch, t]);

  useEffect(() => {
    const skip = currentPage * rowsPerPage;
    dispatch(
      fetchComplaints({
        skip,
        take: rowsPerPage,
        searchText: searchText || undefined,
        status: filters.status || undefined,
        priority: filters.priority || undefined,
        type: filters.type || undefined,
      }),
    );
  }, [
    dispatch,
    searchText,
    currentPage,
    rowsPerPage,
    filters.status,
    filters.priority,
    filters.type,
  ]);

  return (
    <>
      <Helmet>
        <title>{t("complaints")} | CvPlus</title>
      </Helmet>
      <Box sx={{ m: { xs: 0, md: 1 }, px: { xs: 0, md: 2 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {canCreate ? (
            <Tooltip title={t("addNew")}>
              <Button
                color="secondary"
                variant="contained"
                component={RouterLink}
                to="/app/complaints/new"
              >
                <Add />
              </Button>
            </Tooltip>
          ) : (
            <span />
          )}
        </Stack>

        <ComplaintFilters filters={filters} onChange={setFilters} />

        <Grid container sx={{ mt: 1 }}>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell>{t("sno")}</StyledTableCell>
                  <StyledTableCell>{t("employee")}</StyledTableCell>
                  <StyledTableCell>{t("subject")}</StyledTableCell>
                  <StyledTableCell>{t("type")}</StyledTableCell>
                  <StyledTableCell>{t("priority")}</StyledTableCell>
                  <StyledTableCell>{t("status")}</StyledTableCell>
                  <StyledTableCell>{t("openedOn")}</StyledTableCell>
                  <StyledTableCell>{t("actions")}</StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {loading === "pending" ? (
                  <TableSkeleton numRows={5} numColumns={8} />
                ) : complaints.length === 0 ? (
                  <StyledTableRow>
                    <StyledTableCell colSpan={8} align="center">
                      <Typography variant="body2" color="textSecondary">
                        {t("noComplaintsFound")}
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                ) : (
                  complaints.map((row, index) => (
                    <StyledTableRow key={row._id}>
                      <StyledTableCell>
                        {currentPage * rowsPerPage + index + 1}
                      </StyledTableCell>
                      <StyledTableCell>
                        {`${row.employee?.givenName ?? ""} ${row.employee?.surName ?? ""}`.trim()}
                        {row.employee?.passportNumber
                          ? ` (${row.employee.passportNumber})`
                          : ""}
                      </StyledTableCell>
                      <StyledTableCell>{row.subject}</StyledTableCell>
                      <StyledTableCell>{te(row.type)}</StyledTableCell>
                      <StyledTableCell>{te(row.priority)}</StyledTableCell>
                      <StyledTableCell>
                        <ComplaintStatusChip status={row.status} />
                      </StyledTableCell>
                      <StyledTableCell>
                        {row.createdAt
                          ? format(new Date(row.createdAt), "dd-MMM-yyyy HH:mm")
                          : "-"}
                      </StyledTableCell>
                      <StyledTableCell>
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            color="primary"
                            component={RouterLink}
                            to={`/app/complaints/${row._id}`}
                            size="small"
                          >
                            <RemoveRedEye />
                          </IconButton>
                          {isPrivilegedTransaction(
                            roles,
                            RoleTypes.EditComplains,
                          ) &&
                            row.status !== "closed" &&
                            row.status !== "confirmed" && (
                              <IconButton
                                color="secondary"
                                component={RouterLink}
                                to={`/app/complaints/${row._id}/edit`}
                                size="small"
                              >
                                <Edit />
                              </IconButton>
                            )}
                        </Stack>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Paging
            total={totalCount}
            rowsPerPage={rowsPerPage}
            currentPage={currentPage}
            setRowsPerPage={setRowsPerPage}
            setCurrentPage={setCurrentPage}
          />
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            {t("total")}: {totalCount}
          </Typography>
        </Grid>
      </Box>
      {success?.message && (
        <Toast severity="success">{success.message}</Toast>
      )}
      {error?.message && <Toast severity="error">{error.message}</Toast>}
    </>
  );
};

export default ComplaintsList;
